import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import Link from 'next/link';
import { useDispatch } from 'react-redux';
// eslint-disable-next-line import/no-extraneous-dependencies
import Masonry from '@mui/lab/Masonry';
import isEmptyish from '@tmap/mmm-core/isEmptyish';
import useView from '@tmap/mmm-core/redux/views/useView';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Card } from '@tmap/mmm-style-guide/src/Card';
import { Paper } from '@tmap/mmm-style-guide/src/Paper';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { MapsUgcOutlined } from '@tmap/mmm-style-guide/src/Icon';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { useMediaQuery } from '@tmap/mmm-style-guide/src/useMediaQuery';
import { useTheme } from '@tmap/mmm-style-guide/src/Theming';

import useDocumentCache from '@tmap/mmm-core/redux/documentCache/useDocumentCache';
import { CACHE_DOCUMENTS } from '@tmap/mmm-core/redux/documentCache/actionTypes';
import { toKey, types } from '@tmap/mmm-core/entityKey';
import {
  findContributions,
  getContributionBySlug,
  getPaginatedCommunityQuestionsView,
} from '../../actions/contributions';
import Metatags from '../../components/metatags';
import buildPageTitle from '../../lib/community/buildContentPageTitle';

import FullContribution from '../../components/communityContent/fullContribution';
import FullWrapper from '../../layouts/fullWrapper';

import { CONTRIBUTIONS_CACHE, QUESTIONS_CACHE } from '../../actions/cacheIndex';
import ContributionCard from '../../components/communityContent/contributionCard';
import ContributionCardWide from '../../components/communityContent/contributionCardWide';
import CommentsWidget from '../../components/commentsWidget';

const BackNavContainer = styled(Grid)(() => ({
  maxWidth: '1024px',
  marginLeft: 'auto',
  marginRight: 'auto',
  minHeight: '96px',
  marginTop: 'auto',
  marginBottom: 'auto',
  paddingLeft: '8px',
  paddingRight: '8px',
}));

const CommentContainer = styled(Box)(({ theme }) => ({
  margin: '0 auto',
  maxWidth: '840px',
  backgroundColor: 'white',
  borderRadius: '0px 0px 8px 8px',
  paddingLeft: '14%',
  paddingRight: '14%',
  [theme.breakpoints.only('xs')]: { paddingLeft: '3%', paddingRight: '3%' },
}));

const LimitedGrid = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.between('1024px', '1060px')]: { minWidth: '215px', maxWidth: '255px' },
  [theme.breakpoints.between('900px', '1024px')]: { minWidth: '215px', maxWidth: '215px' },
  [theme.breakpoints.between('600px', '900px')]: { minWidth: '296px', maxWidth: '320px' },
  paddingLeft: '8px',
  paddingRight: '8px',
  marginLeft: 'auto',
  marginRight: 'auto',
}));

function ExploreMorePostsLikeThis(props) {
  const {
    _id,
    communityId,
    type,
  } = props;

  const MAX_CLICKS = 5;
  const EXPLORABLES_PAGE_SIZE = 8;
  const EXPLORABLES_DISPLAY_LIMIT = EXPLORABLES_PAGE_SIZE * MAX_CLICKS;
  const [numExplorablesShown, setNumExplorablesShown] = useState(EXPLORABLES_PAGE_SIZE);
  const typeFilter = useMemo(
    () => {
      const originalTypeFilter = (type ? { type } : { $or: [{ type: 'default' }, { type: null }] });
      return (type !== 'question' ? originalTypeFilter : { type: { $ne: 'question' } });
    },
    [type],
  );
  const isQuestionPost = (type === 'question');
  const [contributionIdsToExplore] = useView(
    ['contributions-explore', _id],
    findContributions(communityId, _id, numExplorablesShown, undefined, typeFilter),
  );
  const contributionsToExplore = useDocumentCache(CONTRIBUTIONS_CACHE, contributionIdsToExplore);
  const handleSeeMoreClick = () => {
    if (
      numExplorablesShown < EXPLORABLES_DISPLAY_LIMIT
    ) setNumExplorablesShown(numExplorablesShown + EXPLORABLES_PAGE_SIZE);
  };

  const explorablesGridRows = (contributionsToExplore ?? []).map(
    (explorable, i) => (
      <LimitedGrid item key={`exgi-${i}`}>
        <ContributionCard
          contribution={explorable}
          entityUrl={`/c/${explorable.slug}`}
          showMenuNearText
          showAuthor={false}
          showDate={false}
          showTopics={false}
          squashVerticalPadding
          showCardBorder={false}
          showPinned={false}
          allowNonUniformImageHeights
          width={320}
          previewTextMaxLen={128}
          lazyProps={{ height: '50vh' }}
          pageId='post'
          placementId={`related-posts-${i}`}
          sx={{
            maxWidth: '320px',
            paddingLeft: '0',
            paddingRight: '0',
            margin: '0 auto',
          }}
        />
      </LimitedGrid>
    ),
  );

  return isEmptyish(contributionsToExplore)
    ? null
    : (
      <Box display='flex' alignItems='center' justifyContent='center'>
        <Grid container maxWidth='1024px' justifyContent='center' sx={{ marginBottom: '70px' }}>
          {!isQuestionPost && (
            <Grid item xs={12} padding={3}>
              <Typography
                variant='subtitle1'
                color='textPrimary'
                textAlign='center'
                sx={{ fontWeight: 500 }}
              >Explore more posts like this
              </Typography>
            </Grid>
          )}
          {isQuestionPost && (
            <Grid item xs={12} padding={3}>
              <Typography
                variant='h6'
                color='textPrimary'
                textAlign='center'
                sx={{ fontWeight: 700 }}
              >Explore more posts from this community
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Masonry
              columns={{ xs: 1, sm: 2, md: 4 }}
              spacing={1}
              sx={{
                alignContent: 'center',
                maxWidth: '1024px',
                overflow: 'hidden', // prevents overflowing page on mobile for slow masonry load
              }}
            >
              {explorablesGridRows}
            </Masonry>
          </Grid>
          <Grid item xs={12} md={6} lg={4} sx={{ paddingTop: 4, paddingLeft: '3%', paddingRight: '3%' }}>
            <Button
              fullWidth
              variant='outlined'
              color='inverted'
              disabled={
                (
                  numExplorablesShown >= EXPLORABLES_DISPLAY_LIMIT
                ) || (
                  contributionsToExplore.length < numExplorablesShown
                )
              }
              onClick={handleSeeMoreClick}
            >See More
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
}

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? theme.palette.primary.veryLight : theme.palette.primary.dark,
  padding: theme.spacing(2, 3),
  borderRadius: '12px',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6),
  },
}));

const PaddedContainer = styled(Box)(({ theme }) => ({
  paddingLeft: '16px',
  paddingRight: '16px',
  marginLeft: 'auto',
  marginRight: 'auto',
  [theme.breakpoints.up('xl')]: {
    paddingLeft: '8px',
    paddingRight: '8px',
  },
}));

const BorderedQuestionGrid = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  // FYI (may have to change when grid changes): nth last of type 2 bc button is always last in grid
  '&:not(:nth-last-of-type(2))': {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
}));

const qPlus1 = (qCount) => (qCount ?? 0) + 1;
const pluralize = (wordStr, testVal) => `${wordStr}${testVal ? '' : 's'}`;

function ContentPage(props) {
  const { contribution } = props;
  const canonicalUrl = `${process.env.NEXT_PUBLIC_CANONICAL_DOMAIN}/c/${contribution.slug}`;

  // places SSR-generated contribution in cache so they can be modified
  const dispatch = useDispatch();
  const [cacheDoc] = useDocumentCache(CONTRIBUTIONS_CACHE, [contribution._id]);
  const doc = cacheDoc || contribution;
  useEffect(() => {
    dispatch({
      type: CACHE_DOCUMENTS,
      slice: CONTRIBUTIONS_CACHE,
      docs: [contribution],
    });
  }, [contribution, dispatch]);

  const qPage = 1;
  const qLimit = 3;
  const qOffset = ((qPage) - 1) * qLimit;
  const questionFilters = useMemo(
    () => ({ communityId: doc?.communityId, type: 'question', _id: { $ne: doc?._id } }),
    [doc?.communityId, doc?._id],
  );
  const [questionIds, qView] = useView(
    ['questions', doc?.communityId],
    getPaginatedCommunityQuestionsView(
      qLimit,
      qOffset,
      'new',
      questionFilters,
    ),
    { page: qPage, squashPages: true },
  );
  const questions = useDocumentCache(
    QUESTIONS_CACHE,
    (questionIds ?? []).slice(0, qLimit),
  ).filter((contrib) => !contrib.deleted);

  const isMobile = useMediaQuery(useTheme().breakpoints.down('md'));
  const isQuestion = doc.type === 'question';

  return (
    <>
      <Metatags
        title={buildPageTitle(doc)}
        canonical={canonicalUrl}
        image={doc?.media?.[0]?.type === 'video' ? doc?.media?.[0]?.thumbnailUrl : doc?.media?.[0]?.url}
        video={doc?.media?.[0]?.type === 'video' ? doc?.media?.[0]?.url : undefined}
      />
      <FullWrapper headerOffset>
        <Grid container direction='row' alignItems='center' sx={{ backgroundColor: (theme) => theme.palette.grey[50], marginTop: -4.5 }}>
          <Grid item xs sx={{ backgroundColor: 'inherit', marginBottom: '30px' }}>
            {doc.community?.slug && (
              <BackNavContainer container alignItems='center'>
                <Grid item xs px={1}>
                  <Typography variant='body2' color='primary'>
                    &lt; Back to <Link href='/moving-living'>Communities</Link> / <Link href={`/moving-living/${doc.community.slug}`}>{doc.community.label}</Link>
                  </Typography>
                </Grid>
              </BackNavContainer>
            )}
            {isQuestion && (
              <Grid
                container
                alignItems='flex-end'
                spacing={2}
                marginBottom={4}
                sx={{
                  maxWidth: '840px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  paddingLeft: '0px',
                  paddingRight: '16px',
                }}
              >
                <Grid item xs={12} md>
                  <Typography component='div' variant='h5' fontSize='24px' fontWeight={700} marginBottom={1}>Ask a question about {doc?.community?.label}</Typography>
                  <Typography component='span' variant='body1' fontSize='16px' fontWeight={400} color='textSecondary'>Get answers to your questions from the people who live in {doc?.community?.locationText}.</Typography>
                </Grid>
                <Grid item sx={{ textAlign: 'right' }}>
                  <Button
                    variant='outlined'
                    color='inverted'
                    sx={{ marginTop: { xs: 0.5, md: 1 } }}
                    href={`/moving-living/${doc?.community?.slug}/questions`}
                  >Browse all questions
                  </Button>
                </Grid>
              </Grid>
            )}
            <Paper sx={{
              maxWidth: '840px',
              marginLeft: 'auto',
              marginRight: 'auto',
              boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.12), 0px 4px 33px rgba(0, 0, 0, .1)',
              borderRadius: '8px',
            }}
            >
              <FullContribution doc={doc} />
              <CommentContainer>
                <CommentsWidget
                  contentId={doc.communityId}
                />
              </CommentContainer>
            </Paper>
          </Grid>
          {isQuestion && (
            <Grid item xs={12} sx={{ backgroundColor: 'inherit', marginBottom: '43px' }}>
              <PaddedContainer display='flex' alignItems='center' justifyContent='center'>
                <StyledCard
                  elevation={0}
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    marginTop: '24px',
                    maxWidth: '840px',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                  }}
                >
                  <Grid container alignItems='center' spacing={2}>
                    <Grid item xs={12} md='auto' sx={{ textAlign: 'center' }}>
                      <MapsUgcOutlined
                        sx={{ fontSize: { xs: '36px', sm: '28px' }, color: (theme) => theme.palette.green.light, marginBottom: '-4px' }}
                      />
                    </Grid>
                    <Grid item xs sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                      <Typography component='span' color='common.white' fontWeight={600} size='16px' lineHeight='28px'>Can’t quite find what you’re looking for?</Typography>
                    </Grid>
                    <Grid item xs={12} md='auto'>
                      <Button
                        variant='contained'
                        disableElevation
                        sx={{
                          width: { xs: '100%', md: '199px' },
                          minWidth: '199px',
                          margin: 0,
                          borderRadius: '4px',
                          color: (theme) => theme.palette.primary.contrastText,
                          '&.MuiButtonBase-root': {
                            border: 'solid 1px rgba(255, 255, 255, .6)',
                            background: (theme) => theme.palette.primary.main,
                          },
                        }}
                        href={`/moving-living/${doc?.community?.slug}/questions`}
                      >Browse all {`${qPlus1(qView?.paging?.totalDocs)} ${pluralize('question', qPlus1(qView?.paging?.totalDocs) === 1)}`}
                      </Button>
                    </Grid>
                    <Grid item xs={12} md='auto'>
                      <Button
                        variant='contained'
                        disableElevation
                        color='green'
                        sx={{
                          width: { xs: '100%', md: '168px' },
                          minWidth: '168px',
                          margin: 0,
                          '&.MuiButtonBase-root': {
                            border: (theme) => `solid 1px ${theme.palette.green.light}`,
                            background: (theme) => theme.palette.green.light,
                          },
                        }}
                        href={`/moving-living/${doc?.community?.slug}/post?type=question`}
                      >Ask a question
                      </Button>
                    </Grid>
                  </Grid>
                </StyledCard>
              </PaddedContainer>
            </Grid>
          )}
        </Grid>
        {isQuestion && (
          <>
            <Grid container direction='row' id='exploreMoreTitle' maxWidth='1024px' justifyContent='center' margin='0px auto 32px auto' padding={3}>
              <Grid item xs={12} md={6} lg={4}>
                <PaddedContainer display='flex' alignItems='center' justifyContent='center'>
                  <Typography
                    variant='h6'
                    color='textPrimary'
                    textAlign='center'
                    sx={{ fontWeight: 700 }}
                  >Explore More Like This
                  </Typography>
                </PaddedContainer>
              </Grid>
            </Grid>
            <PaddedContainer display='flex' alignItems='center' justifyContent='center'>
              {questions && questions?.length > 0 && (
                <Grid container direction='row' maxWidth='1024px' justifyContent='center' margin='0px auto 32px auto'>
                  {questions.map((question, i) => (
                    <BorderedQuestionGrid item xs={12} key={`q-${question._id}`}>
                      {isMobile ? (
                        <ContributionCard
                          showCardBorder={false}
                          wantAnswerButton={false}
                          contribution={question}
                          entityUrl={`/c/${question.slug}`}
                          pageId='community'
                          placementId={`question-${i}`}
                        />
                      ) : (
                        <ContributionCardWide
                          showCardBorder={false}
                          wantAnswerButton={false}
                          contribution={question}
                          entityUrl={`/c/${question.slug}`}
                          pageId='community'
                          placementId={`question-${i}`}
                        />
                      )}
                    </BorderedQuestionGrid>
                  ))}
                  <Grid item xs={12}>
                    <Button
                      loading={qView?.isLoading}
                      variant='outlined'
                      fullWidth
                      sx={{
                        minWidth: '100%',
                        width: '100%',
                        backgroundColor: (sxTheme) => sxTheme.palette.grey[100],
                      }}
                      href={`/moving-living/${doc?.community?.slug}/questions`}
                    >See All Questions
                    </Button>
                  </Grid>
                </Grid>
              )}
            </PaddedContainer>
          </>
        )}
        <ExploreMorePostsLikeThis
          _id={doc?._id}
          communityId={doc?.communityId}
          type={doc?.type}
        />
      </FullWrapper>
    </>
  );
}

export const getServerSideProps = async (context) => {
  const { slug } = context.query;
  const notFound = { notFound: true };

  if (isEmptyish(slug)) return notFound;

  const result = await getContributionBySlug(slug);

  if (result.status === 404) {
    return notFound;
  }

  const analyticsProps = {
    pageName: 'contribution',
    entityKey: toKey(types.CONTRIBUTION, result.data._id),
  };
  return { props: { contribution: result.data, analyticsProps } };
};

export default ContentPage;
